import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Glyphicon, Button } from "react-bootstrap";
import { getLatestCapLocationsAll } from "../utils/bedrockAPI";
import "./AdminCapMap.css";
import icon2x from "leaflet/dist/images/marker-icon-2x.png";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
    iconRetinaUrl: icon2x,
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

/** Meters; worse-than-this readings are treated as low accuracy when the toggle is off. */
const LOW_ACCURACY_THRESHOLD_M = 200;

function geomToLatLng(geom) {
    if (!geom || typeof geom !== "object") return null;
    const coords = geom.coordinates;
    if (geom.type === "Point" && Array.isArray(coords) && coords.length >= 2) {
        const [lng, lat] = coords;
        if (typeof lat === "number" && typeof lng === "number") return [lat, lng];
    }
    if (geom.type === "MultiPoint" && Array.isArray(coords) && coords.length > 0) {
        const [lng, lat] = coords[0];
        if (typeof lat === "number" && typeof lng === "number") return [lat, lng];
    }
    return null;
}

function formatWhen(iso) {
    if (iso == null || iso === "") return "—";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function FitBounds({ positions }) {
    const map = useMap();
    useEffect(() => {
        if (!positions || positions.length === 0) return;
        if (positions.length === 1) {
            map.setView(positions[0], 12);
            return;
        }
        const bounds = L.latLngBounds(positions);
        map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
    }, [map, positions]);
    return null;
}

function InvalidateMapSize({ triggerKey }) {
    const map = useMap();
    useEffect(() => {
        // Map is rendered in a tab pane; defer resize so Leaflet measures visible dimensions.
        const t1 = setTimeout(() => map.invalidateSize(), 0);
        const t2 = setTimeout(() => map.invalidateSize(), 200);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [map, triggerKey]);
    return null;
}

export default function AdminCapMap({ caps }) {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    /** When false (default), hide caps whose reported accuracy is greater than 200 m. */
    const [showLowAccuracy, setShowLowAccuracy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setError(null);
        getLatestCapLocationsAll()
            .then((data) => {
                if (!cancelled) setRows(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                if (!cancelled) {
                    setRows([]);
                    setError(e && e.message ? e.message : "Failed to load locations");
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const capById = useMemo(() => {
        const m = new Map();
        if (caps && Array.isArray(caps)) {
            for (let i = 0; i < caps.length; i++) {
                const c = caps[i];
                if (c && c.capId) m.set(c.capId, c);
            }
        }
        return m;
    }, [caps]);

    const allMarkers = useMemo(() => {
        if (!rows) return [];
        const out = [];
        for (let i = 0; i < rows.length; i++) {
            const loc = rows[i];
            const pos = geomToLatLng(loc.geom);
            if (!pos) continue;
            out.push({ loc, position: pos, key: loc.capLocationId || `${loc.capId}-${i}` });
        }
        return out;
    }, [rows]);

    const visibleMarkers = useMemo(() => {
        if (showLowAccuracy) return allMarkers;
        return allMarkers.filter((m) => {
            const a = m.loc.accuracy;
            if (a == null || a === "") return true;
            const n = Number(a);
            if (Number.isNaN(n)) return true;
            return n <= LOW_ACCURACY_THRESHOLD_M;
        });
    }, [allMarkers, showLowAccuracy]);

    const positions = useMemo(() => visibleMarkers.map((m) => m.position), [visibleMarkers]);

    // Initial viewport covers the full US extent, including Alaska and Hawaii.
    const usBounds = [
        [18.5, -179.5], // southwest
        [72.5, -66.0],  // northeast
    ];

    if (rows === null) {
        return (
            <div style={{ padding: "24px" }}>
                <Glyphicon glyph="refresh" style={{ marginRight: "8px" }} />
                Loading cap locations…
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ padding: "24px", color: "#a94442" }}>
                {error}
            </div>
        );
    }

    if (allMarkers.length === 0) {
        return (
            <div style={{ padding: "24px" }}>
                No locations with valid coordinates returned.
            </div>
        );
    }

    if (visibleMarkers.length === 0) {
        return (
            <div style={{ padding: "24px", width: "100%" }}>
                <p style={{ marginBottom: "12px" }}>
                    All {allMarkers.length} location{allMarkers.length === 1 ? "" : "s"} have accuracy worse than {LOW_ACCURACY_THRESHOLD_M} m and are hidden by default.
                </p>
                <Button bsStyle="primary" bsSize="small" onClick={() => setShowLowAccuracy(true)}>
                    <Glyphicon glyph="eye-open" style={{ marginRight: "6px" }} />
                    Show low-accuracy locations
                </Button>
            </div>
        );
    }

    return (
        <div className="admin-cap-map">
            <div style={{ marginBottom: "12px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "12px" }}>
                <p style={{ margin: 0 }}>
                    Latest reported position per cap ({visibleMarkers.length}
                    {showLowAccuracy ? "" : ` of ${allMarkers.length}`} on map
                    {!showLowAccuracy && allMarkers.length > visibleMarkers.length
                        ? `; ${allMarkers.length - visibleMarkers.length} hidden (low accuracy)`
                        : ""}
                    ).
                </p>
                <Button
                    bsStyle={showLowAccuracy ? "info" : "default"}
                    bsSize="small"
                    onClick={() => setShowLowAccuracy(!showLowAccuracy)}
                >
                    <Glyphicon glyph={showLowAccuracy ? "eye-close" : "eye-open"} style={{ marginRight: "6px" }} />
                    {showLowAccuracy
                        ? `Hide low-accuracy`
                        : `Show low-accuracy`}
                </Button>
            </div>
            <div style={{ height: "min(70vh, 640px)", width: "100%", borderRadius: "4px", overflow: "hidden", border: "1px solid #ddd" }}>
                <MapContainer
                    bounds={usBounds}
                    boundsOptions={{ padding: [24, 24] }}
                    style={{ height: "100%", width: "100%" }}
                    scrollWheelZoom
                >
                    <InvalidateMapSize triggerKey={`${visibleMarkers.length}-${showLowAccuracy ? "all" : "filtered"}`} />
                    <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <FitBounds positions={positions} />
                    {visibleMarkers.map(({ loc, position, key }) => {
                        const cap = loc.capId ? capById.get(loc.capId) : null;
                        const nameFromApi = loc.capName ?? loc.cap_name;
                        const capName = nameFromApi || (cap && cap.name) || "Unknown cap";
                        const nh = loc.neighborhoodName ?? loc.neighborhood_name;
                        const neighborhoodName = nh != null && nh !== ""
                            ? nh
                            : (cap && cap.neighborhood ? cap.neighborhood : null);
                        const bn = loc.builderName ?? loc.builder_name;
                        const builderName = bn != null && bn !== "" ? bn : null;
                        return (
                            <Marker key={key} position={position}>
                                <Popup>
                                    <div className="admin-cap-map-popup">
                                        <div className="admin-cap-map-popup__title">
                                            {loc.capId ? (
                                                <Link to={`/caps/${loc.capId}`}>{capName}</Link>
                                            ) : (
                                                capName
                                            )}
                                        </div>
                                        {neighborhoodName ? (
                                            <div className="admin-cap-map-popup__block">
                                                <div className="admin-cap-map-popup__label">Neighborhood</div>
                                                <div className="admin-cap-map-popup__value">{neighborhoodName}</div>
                                            </div>
                                        ) : null}
                                        {builderName ? (
                                            <div className="admin-cap-map-popup__block">
                                                <div className="admin-cap-map-popup__label">Builder</div>
                                                <div className="admin-cap-map-popup__value">{builderName}</div>
                                            </div>
                                        ) : null}
                                        <div className="admin-cap-map-popup__block">
                                            <div className="admin-cap-map-popup__label">Location time</div>
                                            <div className="admin-cap-map-popup__value">{formatWhen(loc.locationDate)}</div>
                                        </div>
                                        <div className="admin-cap-map-popup__block">
                                            <div className="admin-cap-map-popup__label">Accuracy</div>
                                            <div className="admin-cap-map-popup__value">
                                                {loc.accuracy != null && loc.accuracy !== ""
                                                    ? `${Number(loc.accuracy).toFixed(1)} m`
                                                    : "—"}
                                            </div>
                                        </div>
                                    </div>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MapContainer>
            </div>
        </div>
    );
}
